// dialogService — app-wide in-app dialog queue (the replacement for native alert()/confirm()).
// ---------------------------------------------------------------------------------------------
// A module-level singleton queue that ANY code can drive without React context:
// call confirmDialog() or alertDialog() and await the result. <DialogHost/>
// (mounted once at the app root) subscribes to this queue and renders each dialog
// via ModalBackdrop.
//
// Why a singleton, not a React context/hook: several callers (usePasswordRecovery,
// useUserAdminHandlers) are pure state-bag factories, not React components, so they
// cannot consume context. A plain importable function works in components, factories,
// and early app-load effects alike.
//
// Native confirm() is synchronous and returns a boolean inline; this is asynchronous,
// so callers `await confirmDialog(...)` and gate on the resolved boolean. Backdrop
// click / Escape resolve a confirm as false (cancel) and dismiss an alert.

export interface DialogOptions {
  /** Optional bold heading shown above the message. */
  title?: string;
  /** Body text. Newlines are preserved (rendered with whitespace-pre-line). */
  message: string;
  /** Label for the affirmative button. Defaults: "Confirm" (confirm) / "OK" (alert). */
  confirmLabel?: string;
  /** Label for the cancel button (confirm only). Default "Cancel". */
  cancelLabel?: string;
  /** Render the affirmative button in the danger (red) style. */
  danger?: boolean;
  /** Prompt only: placeholder for the text input. */
  placeholder?: string;
  /** Render the modal with a draggable title bar (via ModalBackdrop). */
  draggable?: boolean;
  /**
   * Prompt only: minimum trimmed character count required before the affirmative
   * button enables. When set (> 0) the prompt is MANDATORY — the cancel button is
   * hidden and backdrop/Escape can't dismiss it, so the caller always gets text.
   */
  minLength?: number;
}

export type DialogKind = "confirm" | "alert" | "prompt";
// A confirm/alert resolves a boolean; a prompt resolves the typed string, or null
// if cancelled/dismissed.
export type DialogResult = boolean | string | null;

export interface ActiveDialog extends DialogOptions {
  id: number;
  kind: DialogKind;
}

interface QueuedDialog extends ActiveDialog {
  resolve: (value: DialogResult) => void;
}

type Listener = (active: ActiveDialog | null) => void;

let queue: QueuedDialog[] = [];
let listeners: Listener[] = [];
let nextId = 1;

function toActive(d: QueuedDialog): ActiveDialog {
  // Expose everything except the internal resolver.
  return {
    id: d.id,
    kind: d.kind,
    title: d.title,
    message: d.message,
    confirmLabel: d.confirmLabel,
    cancelLabel: d.cancelLabel,
    danger: d.danger,
    placeholder: d.placeholder,
    draggable: d.draggable,
    minLength: d.minLength,
  };
}

function current(): ActiveDialog | null {
  return queue.length > 0 ? toActive(queue[0]) : null;
}

function emit(): void {
  const active = current();
  for (const listener of listeners) listener(active);
}

/** The currently-visible dialog (front of the queue), or null. */
export function getActiveDialog(): ActiveDialog | null {
  return current();
}

/**
 * Subscribe to active-dialog changes. Fires immediately with the current value.
 * Returns an unsubscribe function.
 */
export function subscribeDialogs(listener: Listener): () => void {
  listeners.push(listener);
  listener(current());
  return function unsubscribe() {
    listeners = listeners.filter((l) => l !== listener);
  };
}

function enqueue(kind: DialogKind, options: DialogOptions): Promise<DialogResult> {
  return new Promise<DialogResult>((resolve) => {
    queue.push({ ...options, id: nextId++, kind, resolve });
    // Only the front dialog is visible; a second one waits until the first responds.
    if (queue.length === 1) emit();
  });
}

/** Show a confirm dialog. Resolves true if the user confirms, false otherwise. */
export function confirmDialog(options: DialogOptions): Promise<boolean> {
  return enqueue("confirm", options).then((v) => v === true);
}

/** Show an alert/notice dialog. Resolves once the user dismisses it. */
export function alertDialog(options: DialogOptions): Promise<void> {
  return enqueue("alert", options).then(() => undefined);
}

/**
 * Show a prompt dialog with a multi-line text input. Resolves the entered text on
 * confirm, or null if cancelled / dismissed (backdrop / Escape).
 */
export function promptDialog(options: DialogOptions): Promise<string | null> {
  return enqueue("prompt", options).then((v) => (typeof v === "string" ? v : null));
}

/** Resolve the dialog with the given id and advance the queue. Called by DialogHost. */
export function respondToDialog(id: number, value: DialogResult): void {
  const idx = queue.findIndex((d) => d.id === id);
  if (idx === -1) return;
  const [done] = queue.splice(idx, 1);
  done.resolve(value);
  emit();
}

/** Test helper — clear all pending dialogs (resolving each as false). */
export function __resetDialogs(): void {
  const pending = queue;
  queue = [];
  for (const d of pending) d.resolve(false);
  emit();
}
